/**
 * SSE streaming client for Avatar chat responses.
 * Expects proposal-style chunks: { type: 'token'|'done'|'error', text?, reason? }
 */

import { appConfig, log } from '../config/env';

export interface StreamChunk {
  type: 'token' | 'done' | 'error';
  text?: string;
  reason?: string;
}

export interface StreamOptions {
  publishableKey: string;
  message: string;
  signal?: AbortSignal;
  /** Override for tests / harness */
  endpoint?: string;
  onChunk: (chunk: StreamChunk) => void;
}

/**
 * Streams an Avatar reply via text/event-stream.
 * Falls back to mock stream only when enableMockSse is true and strictSse is false.
 */
export async function streamAvatarReply(options: StreamOptions): Promise<void> {
  const endpoint = options.endpoint || appConfig.apiUrl;

  try {
    log('debug', 'Opening SSE stream', {
      endpoint,
      channel: appConfig.channel,
    });

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Channel: appConfig.channel,
      },
      body: JSON.stringify({
        publishableKey: options.publishableKey,
        message: options.message,
        env: appConfig.env,
      }),
      signal: options.signal,
    });

    if (!response.ok || !response.body) {
      throw new Error(`Stream unavailable (${response.status})`);
    }

    await parseEventStream(response.body, options.onChunk, options.signal);
  } catch (error) {
    if (options.signal?.aborted) return;

    const canMock = appConfig.enableMockSse && !appConfig.strictSse;
    if (canMock) {
      log('warn', 'Live SSE unavailable — using mock stream', error);
      await mockStream(options.message, options.onChunk, options.signal);
      return;
    }

    const reason =
      error instanceof Error ? error.message : 'Unable to reach Avatar service';
    log('error', 'SSE stream failed', reason);
    options.onChunk({ type: 'error', reason });
  }
}

async function parseEventStream(
  body: ReadableStream<Uint8Array>,
  onChunk: (chunk: StreamChunk) => void,
  signal?: AbortSignal,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finished = false;

  try {
    while (true) {
      if (signal?.aborted) break;
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop() ?? '';

      for (const part of parts) {
        const dataLine = part
          .split('\n')
          .find((line) => line.startsWith('data:'));
        if (!dataLine) continue;

        const raw = dataLine.slice(5).trim();
        if (!raw || raw === ': keepalive') continue;

        if (raw === '[DONE]') {
          onChunk({ type: 'done' });
          finished = true;
          return;
        }

        try {
          const parsed = JSON.parse(raw) as StreamChunk;
          if (
            parsed.type === 'token' ||
            parsed.type === 'done' ||
            parsed.type === 'error'
          ) {
            onChunk(parsed);
            if (parsed.type === 'done' || parsed.type === 'error') {
              finished = true;
              return;
            }
          }
        } catch {
          onChunk({ type: 'token', text: raw });
        }
      }
    }
    if (!finished) {
      onChunk({ type: 'done' });
    }
  } finally {
    reader.releaseLock();
  }
}

async function mockStream(
  userMessage: string,
  onChunk: (chunk: StreamChunk) => void,
  signal?: AbortSignal,
): Promise<void> {
  const reply =
    `Thanks for asking about "${userMessage.trim()}". ` +
    `I'm Avatar AI — I can help you discover, create, and get the most out of your Zetrix Avatar. ` +
    `(${appConfig.env} mock stream — set VITE_AVATAR_API_URL for live SSE.)`;

  const words = reply.split(' ');
  for (let i = 0; i < words.length; i++) {
    if (signal?.aborted) return;
    await delay(28);
    onChunk({
      type: 'token',
      text: (i === 0 ? '' : ' ') + words[i],
    });
  }
  onChunk({ type: 'done' });
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
